import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categoryLinks } from '@/mocks/categories';
import { useNavigate } from 'react-router-dom';

const languages = [
  { code: 'pt', label: 'PT-BR' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

const categoryIcons: Record<string, string> = {
  shoes: 'ri-footprint-line',
  hoodies_sweaters: 'ri-shirt-line',
  t_shirts: 'ri-shirt-line',
  jackets: 'ri-shirt-line',
  pants_shorts: 'ri-shirt-line',
  headwear: 'ri-vip-crown-line',
  sets: 'ri-shirt-line',
  underwear: 'ri-shirt-line',
  jersey: 'ri-medal-line',
  accessories: 'ri-glasses-line',
  bags: 'ri-handbag-line',
  belts: 'ri-scissors-line',
  perfumes: 'ri-drop-line',
  other_stuff: 'ri-sparkling-line',
};

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showLang, setShowLang] = useState(false);
  const [showMobile, setShowMobile] = useState(false);
  const [showMobileCategories, setShowMobileCategories] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setShowLang(false);
  };

  const goHome = () => {
    navigate('/');
    setShowMobile(false);
  };

  const goGuide = () => {
    navigate('/cssbuy-spreadsheet');
    setShowMobile(false);
  };

  const goProducts = () => {
    navigate('/products');
    setShowMobile(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        scrolled ? 'shadow-sm border-b border-background-200' : 'border-b border-background-100'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button
            onClick={goHome}
            className="flex items-center gap-2 text-foreground-950 font-heading text-xl md:text-2xl font-semibold tracking-tight cursor-pointer"
          >
            <span className="text-primary-500">CSSBuy</span>
            <span>Spreadsheet</span>
          </button>

          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={goHome}
              className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('home')}
            </button>

            <div className="relative">
              <button
                onClick={() => { setShowCategories(!showCategories); setShowLang(false); }}
                className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                {t('categories')}
                <i className="ri-arrow-down-s-line text-sm" />
              </button>
              {showCategories && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-white rounded-lg shadow-xl border border-background-200 py-2 max-h-96 overflow-y-auto">
                  {categoryLinks.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                    >
                      <i className={`${categoryIcons[cat.key] || 'ri-arrow-right-circle-line'} text-primary-400 text-base`} />
                      {t(cat.key) || cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={goProducts}
              className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('products')}
            </button>

            <button
              onClick={goGuide}
              className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('shopping_guide')}
            </button>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {t('explore_spreadsheet')}
            </a>

            <div className="relative">
              <button
                onClick={() => { setShowLang(!showLang); setShowCategories(false); }}
                className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-global-line text-base" />
                {languages.find(l => l.code === i18n.language)?.label || 'PT-BR'}
                <i className="ri-arrow-down-s-line text-sm" />
              </button>
              {showLang && (
                <div className="absolute top-full mt-2 right-0 w-36 bg-white rounded-lg shadow-xl border border-background-200 py-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLang(lang.code)}
                      className={`block w-full text-left px-4 py-2 text-sm transition-colors cursor-pointer ${
                        i18n.language === lang.code
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-foreground-700 hover:bg-primary-50'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('buy_now')}
            </a>
          </div>

          <button
            onClick={() => setShowMobile(!showMobile)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`ri-${showMobile ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {showMobile && (
        <div className="md:hidden bg-white border-t border-background-200 px-4 py-5 space-y-5 max-h-[80vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={goHome}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-background-100 rounded-lg text-sm font-medium text-foreground-900 cursor-pointer"
            >
              <i className="ri-home-4-line" />
              {t('home')}
            </button>
            <button
              onClick={goProducts}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-secondary-100 rounded-lg text-sm font-medium text-secondary-900 cursor-pointer"
            >
              <i className="ri-store-2-line" />
              {t('products')}
            </button>
            <button
              onClick={goGuide}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-background-100 rounded-lg text-sm font-medium text-foreground-900 cursor-pointer col-span-2"
            >
              <i className="ri-book-open-line" />
              {t('shopping_guide')}
            </button>
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="block w-full text-center py-3 bg-primary-500 text-white font-medium rounded-lg"
          >
            {t('buy_now')}
          </a>

          <div>
            <button
              onClick={() => setShowMobileCategories(!showMobileCategories)}
              className="w-full flex items-center justify-between py-2 cursor-pointer"
            >
              <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider">{t('categories')}</p>
              <i className={`ri-arrow-down-s-line text-foreground-500 ${showMobileCategories ? 'rotate-180' : ''}`} />
            </button>
            {showMobileCategories && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                {categoryLinks.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-50 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                  >
                    <i className={`${categoryIcons[cat.key] || 'ri-arrow-right-circle-line'} text-primary-400 text-base flex-shrink-0`} />
                    <span className="truncate">{t(cat.key) || cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-background-200 pt-4">
            <p className="text-xs font-medium text-foreground-500 mb-2 uppercase tracking-wider">{t('language')}</p>
            <div className="flex gap-3">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLang(lang.code)}
                  className={`text-sm px-3 py-1.5 rounded-md cursor-pointer ${
                    i18n.language === lang.code ? 'bg-primary-500 text-white' : 'text-foreground-700 bg-background-100'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}